/**
 * Formatierung für die Oberfläche. Beträge laufen ausschließlich über `formatEuro`
 * aus dem Rechenkern (@nkp/shared) — nirgends manuelle Zahlenformatierung von Geld.
 */

import { formatEuro, euroToCent } from '@nkp/shared';
import type { Cent, ISODate } from '@nkp/shared';

export { formatEuro, euroToCent };

const zahl = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const datum = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
});

export function formatZahl(wert: number): string {
  return zahl.format(wert);
}

export function formatQm(wert: number): string {
  return `${zahl.format(wert)} m²`;
}

export function formatProzent(wert: number): string {
  return `${zahl.format(wert)} %`;
}

export function formatDatum(iso: ISODate): string {
  const zeit = Date.parse(iso);
  return Number.isNaN(zeit) ? iso : datum.format(new Date(zeit));
}

export function formatZeitraum(von: ISODate, bis: ISODate): string {
  return `${formatDatum(von)} – ${formatDatum(bis)}`;
}

/**
 * Betragseingabe (de-DE, z. B. "1.234,56") in Cent. Gibt null zurück, wenn die
 * Eingabe kein gültiger Betrag ist — der Aufrufer zeigt dann eine Meldung.
 */
export function parseEuroEingabe(eingabe: string): Cent | null {
  const roh = eingabe.trim().replace(/\s|€/g, '');
  if (roh === '') return null;
  // Mit Komma: Punkte sind Tausendertrenner („1.234,56“). Ohne Komma gilt ein Punkt nur
  // bei Dreiergruppen als Tausendertrenner („1.234“), sonst als Dezimalpunkt („150.50“).
  const normiert = roh.includes(',')
    ? roh.replace(/\./g, '').replace(',', '.')
    : /^-?\d{1,3}(\.\d{3})+$/.test(roh)
      ? roh.replace(/\./g, '')
      : roh;
  if (!/^-?\d+(\.\d{1,2})?$/.test(normiert)) return null;
  return euroToCent(Number(normiert));
}

/**
 * Zahleneingabe (Fläche, Personen, Prozent, Zählerstand) in eine Zahl. Deutsche Schreibweise
 * „1.234,5“ ebenso wie „1234.5“: Steht ein Komma in der Eingabe, sind Punkte Tausendertrenner.
 */
export function parseZahlEingabe(eingabe: string): number | null {
  const ohneLeer = eingabe.trim().replace(/\s/g, '');
  const roh = ohneLeer.includes(',') ? ohneLeer.replace(/\./g, '').replace(',', '.') : ohneLeer;
  if (roh === '') return null;
  const wert = Number(roh);
  return Number.isFinite(wert) ? wert : null;
}

export interface SaldoDarstellung {
  bezeichnung: 'Nachzahlung' | 'Guthaben' | 'Ausgeglichen';
  betrag: string;
  ton: 'warnung' | 'erfolg' | 'neutral';
}

/**
 * Saldo aus Sicht der Abrechnung: > 0 Nachzahlung des Mieters,
 * < 0 Guthaben zugunsten des Mieters.
 */
export function beschreibeSaldo(saldoCent: Cent): SaldoDarstellung {
  if (saldoCent > 0) {
    return { bezeichnung: 'Nachzahlung', betrag: formatEuro(saldoCent), ton: 'warnung' };
  }
  if (saldoCent < 0) {
    return { bezeichnung: 'Guthaben', betrag: formatEuro(-saldoCent), ton: 'erfolg' };
  }
  return { bezeichnung: 'Ausgeglichen', betrag: formatEuro(0), ton: 'neutral' };
}
